import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  ensureUmamiInteractionSignal,
  markUmamiInteractionConfirmed,
  trackUmamiPageview,
} from "@/lib/umamiAnalytics";

const PROTECTED_PREFIXES = [
  "/dashboard",
  "/upload",
  "/integrations",
  "/calls",
  "/customers",
  "/account",
  "/profile",
  "/prepare-next-call",
];

function matchesPrefix(pathname, prefix) {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

function canTrackRoute(pathname, user, loading) {
  if (loading) return false;

  if (pathname === "/admin") return user?.account_type === "owner";
  if (pathname === "/legal-consent") return Boolean(user);

  if (PROTECTED_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix))) {
    return Boolean(user);
  }

  return true;
}

export default function AnalyticsRouteTracker() {
  const location = useLocation();
  const { user, loading } = useAuth();
  const { pathname, search, key } = location;

  useEffect(() => {
    const confirmInteraction = (event) => {
      if (!event?.isTrusted) return;
      markUmamiInteractionConfirmed();
    };

    window.addEventListener("pointerdown", confirmInteraction, { passive: true, capture: true });
    window.addEventListener("touchstart", confirmInteraction, { passive: true, capture: true });
    window.addEventListener("keydown", confirmInteraction, { capture: true });

    // If the user already interacted earlier in this tab/session, make sure the
    // property is attached once the deferred Umami script becomes available.
    ensureUmamiInteractionSignal();

    return () => {
      window.removeEventListener("pointerdown", confirmInteraction, { capture: true });
      window.removeEventListener("touchstart", confirmInteraction, { capture: true });
      window.removeEventListener("keydown", confirmInteraction, { capture: true });
    };
  }, []);

  useEffect(() => {
    if (!canTrackRoute(pathname, user, loading)) return undefined;

    // Let React Router/Auth redirects settle before recording the view. This
    // prevents unauthenticated probes of protected routes (for example
    // /integrations -> /login) from appearing as successful protected views.
    let timer;
    let attempts = 0;

    const sendPageview = () => {
      if (window.location.pathname !== pathname || window.location.search !== search) return;

      ensureUmamiInteractionSignal();
      if (trackUmamiPageview()) return;

      // The Umami script is deferred. On a slow connection it may not be ready
      // when React finishes routing, so retry briefly instead of losing the view.
      attempts += 1;
      if (attempts < 25) timer = window.setTimeout(sendPageview, 160);
    };

    timer = window.setTimeout(sendPageview, 180);
    return () => window.clearTimeout(timer);
  }, [key, pathname, search, user, loading]);

  return null;
}
